import React, { useEffect, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { ShieldCheck, RefreshCw, MapPin, BadgeCheck } from 'lucide-react';
import api, { fileUrl } from './api';
import { useToast } from '../hooks/use-toast';
import { acquireLocation, locHint } from './geo';

// Operative Digital ID card + dynamic QR generator.
const OperativeID = () => {
  const { toast } = useToast();
  const [card, setCard] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [jobId, setJobId] = useState('');
  const [qr, setQr] = useState(null); // {token, expiresAt}
  const [secs, setSecs] = useState(0);
  const [busy, setBusy] = useState(false);
  const [loc, setLoc] = useState({ status: 'idle', lat: null, lng: null, error: null });
  const timer = useRef();

  const getLocation = async () => {
    setLoc((s) => ({ ...s, status: 'acquiring' }));
    const g = await acquireLocation();
    setLoc({ status: g.lat != null ? 'ready' : 'error', lat: g.lat, lng: g.lng, error: g.error, accuracy: g.accuracy });
    return g;
  };

  useEffect(() => {
    api.get('/verify/my-id').then((r) => setCard(r.data)).catch(() => {});
    api.get('/verify/active-jobs').then((r) => { setJobs(r.data); if (r.data[0]) setJobId(r.data[0].jobId); }).catch(() => {});
    getLocation(); // request GPS up front so it's ready before generating
  }, []);

  useEffect(() => {
    if (!qr) return;
    clearInterval(timer.current);
    timer.current = setInterval(() => {
      const left = Math.max(0, Math.round((new Date(qr.expiresAt) - new Date()) / 1000));
      setSecs(left);
      if (left <= 0) { clearInterval(timer.current); setQr(null); }
    }, 1000);
    return () => clearInterval(timer.current);
  }, [qr]);

  const generate = async () => {
    if (!jobId) { toast({ title: 'Select a job first', variant: 'destructive' }); return; }
    setBusy(true);
    try {
      let g = loc;
      if (g.lat == null) g = await getLocation();
      if (g.lat == null) {
        setBusy(false);
        toast({ title: 'Location needed', description: locHint(g.error), variant: 'destructive' });
        return;
      }
      const r = await api.post('/verify/generate-qr', { jobId, lat: g.lat, lng: g.lng, accuracy: g.accuracy });
      setQr(r.data);
    } catch (e) { toast({ title: 'Error', description: e?.response?.data?.detail || 'Could not generate QR.', variant: 'destructive' }); }
    finally { setBusy(false); }
  };

  if (!card) return null;
  return (
    <div className="mt-8" data-testid="operative-id-section">
      <h2 className="font-display text-lg font-bold text-[#1b4f72]">Digital Operative ID</h2>
      <p className="text-sm text-gray-500">Show this on-site. Generate a fresh QR for the client to scan and verify you.</p>
      <div className="mt-4 grid gap-5 lg:grid-cols-2">
        {/* ID card */}
        <div className="overflow-hidden rounded-2xl bg-gradient-to-br from-[#1b4f72] to-[#2b6b95] p-6 text-white shadow-md" data-testid="id-card">
          <div className="flex items-center gap-4">
            <div className="h-20 w-20 shrink-0 overflow-hidden rounded-xl bg-white/20 ring-2 ring-white/40">
              {card.photoFileId ? <img src={fileUrl(card.photoFileId)} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full w-full items-center justify-center"><ShieldCheck size={28} /></div>}
            </div>
            <div className="min-w-0">
              <p className="text-lg font-bold">{card.name}</p>
              <p className="text-sm text-white/80">{card.trade}{card.companyName ? ` · ${card.companyName}` : ''}</p>
              <p className="mt-1 font-mono text-sm tracking-wider text-[#7cc4a0]" data-testid="id-reg">{card.regNumber}</p>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {card.verified && <span className="flex items-center gap-1 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold"><BadgeCheck size={13} /> Verified by EA Lead Solutions</span>}
            {!card.suspended ? <span className="rounded-full bg-[#3a9b5c] px-3 py-1 text-xs font-semibold">Active</span> : <span className="rounded-full bg-red-500 px-3 py-1 text-xs font-semibold">Suspended</span>}
          </div>
          <p className="mt-4 text-[11px] text-white/60">E.A. Lead Solutions · Company No. 16302746</p>
        </div>
        {/* QR generator */}
        <div className="rounded-2xl bg-white p-6 shadow-sm">
          {jobs.length === 0 ? (
            <p className="text-sm text-gray-500">You have no confirmed in-progress jobs to verify right now.</p>
          ) : (
            <>
              <label className="mb-1.5 block text-sm font-medium text-gray-700">Job to verify</label>
              <select data-testid="id-job-select" value={jobId} onChange={(e) => { setJobId(e.target.value); setQr(null); }} className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm">
                {jobs.map((j) => <option key={j.jobId} value={j.jobId}>{j.ref} — {j.title}</option>)}
              </select>
              <div className={`mt-3 flex items-center justify-between gap-2 rounded-lg px-3 py-2 text-xs ${loc.status === 'ready' ? 'bg-green-50 text-green-800' : loc.status === 'acquiring' ? 'bg-blue-50 text-blue-800' : 'bg-amber-50 text-amber-800'}`} data-testid="id-loc-status">
                <span className="min-w-0">{loc.status === 'ready' ? `📍 Location ready${loc.accuracy ? ` (±${Math.round(loc.accuracy)}m)` : ''}` : loc.status === 'acquiring' ? '📍 Getting location…' : '📍 Enable location to generate a QR'}</span>
                {loc.status !== 'ready' && loc.status !== 'acquiring' && <button data-testid="id-retry-loc-btn" onClick={getLocation} className="shrink-0 rounded-full bg-[#1b4f72] px-3 py-1 font-semibold text-white">Enable</button>}
              </div>
              <div className="mt-4 flex flex-col items-center">
                {qr && secs > 0 ? (
                  <>
                    <div className="rounded-xl border-4 border-[#e9f3ec] p-3" data-testid="id-qr"><QRCodeSVG value={qr.token} size={190} level="M" /></div>
                    <p className="mt-2 text-sm font-semibold text-[#3a9b5c]" data-testid="id-qr-countdown">Expires in {Math.floor(secs / 60)}:{String(secs % 60).padStart(2, '0')} · single use</p>
                  </>
                ) : (
                  <div className="flex h-[210px] w-[210px] items-center justify-center rounded-xl bg-gray-50 text-center text-sm text-gray-400">Generate a QR when you arrive on-site</div>
                )}
                <button data-testid="generate-qr-btn" disabled={busy} onClick={generate} className="mt-4 flex items-center gap-2 rounded-full bg-[#1b4f72] px-6 py-2.5 text-sm font-semibold text-white hover:bg-[#163f5c] disabled:opacity-60"><RefreshCw size={15} /> {busy ? 'Generating…' : (qr ? 'Regenerate QR' : 'Generate QR')}</button>
                <p className="mt-2 flex items-center gap-1 text-[11px] text-gray-400"><MapPin size={12} /> Your location is logged each time for security.</p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OperativeID;
